import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { colors } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPeriod(period, groupBy) {
  if (groupBy === 'year') return period;
  const [y, m] = period.split('-');
  return `${MONTH_ABBR[parseInt(m, 10) - 1]} ${y}`;
}

function CustomTooltip({ active, payload, label, groupBy }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: colors.surfaceRaised,
      border: `1px solid ${colors.panelBorder}`,
      borderRadius: '8px',
      padding: '0.6rem 0.9rem',
      fontSize: '0.85rem'
    }}>
      <div style={{ color: colors.textMuted, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ color: colors.accent, fontWeight: 700 }}>
        {payload[0].value} RMA{payload[0].value !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default function OrgRmaTrendChart() {
  const { getToken } = useAuth();
  const [groupBy, setGroupBy] = useState('month');
  const [buckets, setBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/org/analytics/rma-trend?groupBy=${groupBy}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load trend data');
      const data = await res.json();
      setBuckets(
        (data.buckets || []).map(b => ({
          ...b,
          label: formatPeriod(b.period, groupBy)
        }))
      );
    } catch (err) {
      setError(err.message || 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [groupBy, getToken]);

  useEffect(() => { load(); }, [load]);

  const maxCount = buckets.length ? Math.max(...buckets.map(b => b.count), 1) : 1;
  const yMax = Math.ceil(maxCount * 1.25) || 5;

  return (
    <div style={{
      background: colors.surfaceRaised,
      border: `1px solid ${colors.panelBorder}`,
      borderRadius: '16px',
      padding: '1.5rem 1.75rem'
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ color: colors.textStrong, fontWeight: 700, fontSize: '1.05rem' }}>
            Repair Requests Over Time
          </div>
          <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.2rem' }}>
            Total RMAs submitted for your company's devices
          </div>
        </div>

        {/* Month / Year toggle */}
        <div style={{
          display: 'flex',
          background: colors.surface,
          border: `1px solid ${colors.panelBorder}`,
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          {['month', 'year'].map(g => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              style={{
                background: groupBy === g ? colors.accent : 'transparent',
                color: groupBy === g ? '#0B0F0D' : colors.textMuted,
                border: 'none',
                padding: '0.35rem 0.85rem',
                fontSize: '0.8rem',
                fontWeight: groupBy === g ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                letterSpacing: '0.02em'
              }}
            >
              {g === 'month' ? 'Monthly' : 'Yearly'}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      {isLoading ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: '0.85rem' }}>
          Loading…
        </div>
      ) : error ? (
        <div style={{ height: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
          <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>{error}</span>
          <button
            onClick={load}
            style={{ background: colors.accent, color: '#0B0F0D', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
          >
            Retry
          </button>
        </div>
      ) : buckets.length === 0 ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: '0.85rem' }}>
          No RMA data yet for your company.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={buckets} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(147,149,151,0.12)"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: colors.textDim, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval={groupBy === 'month' && buckets.length > 24 ? Math.floor(buckets.length / 12) : 'preserveStartEnd'}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, yMax]}
              tick={{ fill: colors.textDim, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<CustomTooltip groupBy={groupBy} />} cursor={{ stroke: colors.panelBorder, strokeWidth: 1 }} />
            <Line
              type="monotone"
              dataKey="count"
              stroke={colors.accent}
              strokeWidth={2.5}
              dot={buckets.length <= 36 ? { r: 3, fill: colors.accent, stroke: colors.surfaceRaised, strokeWidth: 2 } : false}
              activeDot={{ r: 5, fill: colors.accent, stroke: colors.surfaceRaised, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
